import { Trash2 } from "lucide-react";
import React from "react";

import type { InventoryScanRow } from "@/api/inventory";
import { Button } from "@/components/ui/button";
import { fmtDateTime, fmtTime, ruNum } from "@/lib/format";
import { Dialog } from "@/lib/scan-ui/overlays";

/** Как называется товар в строке скана: имени может не быть у нераспознанного
 *  кода — тогда показываем сам код, иначе строка выглядит пустой. */
function scanTitle(s: InventoryScanRow): string {
  const extra = [s.characteristic_name, s.partia_name ? `п. ${s.partia_name}` : ""]
    .filter(Boolean)
    .join(" · ");
  const name = s.nomenclature_name || s.raw_code || "Скан";
  return extra ? `${name} · ${extra}` : name;
}

/** Сколько в скане: пачки и штуки — разные числа, когда коэффициент не 1
 *  (короб на 6 штук считается одним сканом). */
function scanQtyText(s: InventoryScanRow): string {
  const packs = `${ruNum.format(s.qty_scanned)}`;
  if (!s.unit_coef || s.unit_coef === 1) return `${packs} шт`;
  return `${packs} × ${ruNum.format(s.unit_coef)} = ${ruNum.format(s.qty_base)} шт`;
}

/**
 * Список сканов с действиями: удалить строку и поправить её количество.
 *
 * Компонент ЧИСТЫЙ по данным — ручки дёргает владелец через колбэки. Смена
 * общая, и в ленте лежат ЧУЖИЕ факты: удаление всегда проходит через
 * подтверждение, которое называет автора, время, товар и количество. Без
 * этого «удалить ошибочный скан» превращается в «стереть чужую полку».
 *
 * На завершённой смене (`readOnly`) действий нет вовсе: бэкенд ответит 409,
 * и показывать кнопку, которая гарантированно не сработает, незачем.
 */
export function ScanList({
  scans,
  readOnly,
  busy,
  onDelete,
  onChangeQty,
  emptyText = "Сканов нет.",
}: {
  scans: InventoryScanRow[];
  readOnly?: boolean;
  /** Правка/удаление уже летит — кнопки гасим, чтобы не послать вторую. */
  busy?: boolean;
  onDelete: (scan: InventoryScanRow) => void;
  onChangeQty: (scan: InventoryScanRow, qty: number) => void;
  emptyText?: string;
}): React.ReactElement {
  const [ask, setAsk] = React.useState<InventoryScanRow | null>(null);

  /** Локальное количество, ещё не подтверждённое сервером: без него два
   *  быстрых тапа по «+/−» оба считают от одной и той же устаревшей строки
   *  (пока `busy` не поднялся и ответ сервера не пришёл) и второй тап
   *  теряется. Тап всегда прибавляет/отнимает к ЭТОМУ числу, а не к
   *  `qty_scanned` из пропа. */
  const [overrides, setOverrides] = React.useState<Record<string, number>>({});
  const prevBusy = React.useRef(busy);
  const prevQty = React.useRef<Record<string, number>>({});

  React.useEffect(() => {
    // Сервер прислал по строке НОВОЕ число (не то, что было на предыдущем
    // рендере) — это подтверждение легло, локальное больше не нужно, дальше
    // доверяем пропу. Чинится успешная правка; неудачная — ниже.
    setOverrides((prev) => {
      let next: Record<string, number> | null = null;
      for (const s of scans) {
        const seen = prevQty.current[s.id];
        if (s.id in prev && seen !== undefined && seen !== s.qty_scanned) {
          next ??= { ...prev };
          delete next[s.id];
        }
      }
      return next ?? prev;
    });
    prevQty.current = Object.fromEntries(scans.map((s) => [s.id, s.qty_scanned]));
  }, [scans]);

  React.useEffect(() => {
    // Правка или удаление только что осели (busy: true → false), а прошлый
    // эффект число не подтвердил — значит запрос упал: возвращаемся к
    // настоящему серверному значению, а не оставляем на экране придуманное
    // (баннер ошибки показывает страница, второй текст здесь не нужен).
    if (prevBusy.current && !busy) {
      setOverrides({});
    }
    prevBusy.current = busy;
  }, [busy]);

  if (scans.length === 0) {
    return <p className="p-3 text-center text-sm text-slate-500">{emptyText}</p>;
  }

  return (
    <div className="space-y-1">
      {scans.map((s) => {
        const qty = overrides[s.id] ?? s.qty_scanned;
        const display: InventoryScanRow =
          qty === s.qty_scanned
            ? s
            : { ...s, qty_scanned: qty, qty_base: s.unit_coef && s.unit_coef !== 1 ? qty * s.unit_coef : qty };
        const bump = (delta: number) => {
          const next = Math.max(1, qty + delta);
          setOverrides((prev) => ({ ...prev, [s.id]: next }));
          onChangeQty(s, next);
        };

        return (
          <div key={s.id} className="rounded border p-2">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium leading-tight">{scanTitle(s)}</div>
                <div className="text-[11px] text-slate-500">
                  {s.username}
                  {s.device ? ` · ${s.device}` : ""}
                  {s.at ? ` · ${fmtTime(s.at)}` : ""}
                </div>
              </div>
              <div className="shrink-0 text-right text-[12px] font-semibold">
                {scanQtyText(display)}
              </div>
            </div>
            {!readOnly && (
              <div className="mt-1.5 flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || qty <= 1}
                  title="Меньше на одну"
                  onClick={() => bump(-1)}
                >
                  −
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  title="Больше на одну"
                  onClick={() => bump(1)}
                >
                  +
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto text-red-700"
                  disabled={busy}
                  title="Удалить скан"
                  aria-label={`Удалить скан: ${scanTitle(s)}`}
                  onClick={() => setAsk(s)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {ask && (
        <Dialog
          title="Удалить этот скан?"
          danger
          body={
            <div className="space-y-1">
              <p className="font-medium text-slate-900">{scanTitle(ask)}</p>
              <p>
                {scanQtyText(ask)} · {ask.username}
                {ask.device ? ` · ${ask.device}` : ""}
                {ask.at ? ` · ${fmtDateTime(ask.at)}` : ""}
              </p>
              <p className="text-slate-500">
                Строка исчезнет насовсем, посчитанное по позиции уменьшится.
              </p>
            </div>
          }
          confirmLabel="Удалить"
          confirmBusy={busy}
          onConfirm={() => {
            onDelete(ask);
            setAsk(null);
          }}
          onCancel={() => setAsk(null)}
        />
      )}
    </div>
  );
}
